var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { faEye, faLockKeyhole, faLockKeyholeOpen, faMaximize, faMinimize, faRightLeft, faSquarePen, faStarHalfStroke, faTrashCan, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Link, Stack, Tooltip, Typography, styled, useTheme } from '@mui/material';
import { chain, map, times } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StatusPermission } from '../../enum';
import { useUserPermissionsAPI } from '../app/selectors';
import { ErrorMessageBox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, sortQueuesByName } from '../common';
import { useCurrencySymbol } from '../hooks/useCurrencyOptions';
import { QueueType } from '../multiskill/types';
import { useResourcingContext } from '../report/hooks';
import { DisplayMode } from '../report/resourcing';
import { useDisplayModeParams, useExportAPI, useForbiddenRedirect, usePrintTableAPI, useReportAPI, useReportEffectivePermission, useReportEffectivePlanPermission } from '../report/selectors';
import { SeatExceededIcon, useSeatCapacity } from '../seatCapacity';
import { PLAN_COLORS, WizardStep } from '../wizard/constants';
import { AgentRequirementTable } from './agentRequirement';
import { AgentSalariesTable } from './agentSalaries';
import { getImplicitNestedExpansionItems } from './common/annualSectionTable';
import { getMultiskillCategoryExpansionKey, getQueueExpansionKeysForLob, useMultiskillGroupExpansionKeys, useSelectQueuesByLobPerScenario, useTableRowExpansion } from './common/selectors';
import { getGroupedMultiskillScenarioCellColor, isGroupedMultiskillScenario } from './common/utils';
import { ANNUAL_REPORT_ROWS, BUDGET_ROWS, CALCULATE_DIFF, CURRENCY, DECIMAL_VALUE, HIDE_ROW, INCLUDE_ROLE, MULTISKILL_COLOR, PERCENTAGES, SERVICE_ROWS, getConstraintExpansionKey, getLobExpansionKey, metricTargetMap } from './constants';
import { CostPerWorkTable } from './costPerWork';
import { HiringAheadTable } from './hiringAhead';
import { LearningCurveImpactTable } from './learningCurveImpact';
import { OvertimeCostsTable } from './overtimeCosts';
import { ProductionHoursTable } from './productionHours';
import { SeatsRequiredTable } from './seatsRequired';
import { generateVariances, useAllMultiskillScenariosAPI, useSelectCurrency, useSelectQueuesByCost, useSelectQueuesByName, useSelectRoles, useSelectScenario, useSummaryByDisplayMode, useSummaryChunkSize, useSummaryReportAPI } from './selectors';
import { ServiceStandardSection } from './serviceStandard';
import { StaffBudgetSection } from './staffBudget';
import { SupportStaffSalariesTable, SupportStaffTable } from './supportStaff';
import { ConstraintType, DialogMode } from './types';
export var STICKIES = ['23.75rem', '17rem'];
export var FirstCell = styled(TableCell)(function (_a) {
  var theme = _a.theme,
    paddingLeft = _a["aria-label"];
  return {
    textAlign: 'left',
    minWidth: theme.typography.pxToRem(380),
    '&.MuiTableCell-root': {
      paddingLeft: paddingLeft
    }
  };
});
var EditIconButton = styled(IconButton)({
  '&.Mui-disabled': {
    color: 'inherit',
    opacity: 0.38
  }
});
var HeaderCell = function (_a) {
  var _b = _a.title,
    title = _b === void 0 ? 'Baseline' : _b,
    handleOpenDialog = _a.handleOpenDialog,
    highlightBorder = _a.highlightBorder,
    isMultiskill = _a.isMultiskill,
    color = _a.color,
    scenarioNameHeader = _a.scenarioNameHeader,
    headerColor = _a.headerColor,
    permission = _a.permission,
    unit = _a.unit,
    year = _a.year,
    planId = _a.planId;
  var scenario = useSelectScenario(title);
  var t = useTranslation(['report', 'translation']).t;
  var _c = useSeatCapacity(),
    isScenarioExceeded = _c.isScenarioExceeded,
    openAlertDetails = _c.openAlertDetails;
  var seatsExceeded = scenarioNameHeader && isScenarioExceeded(planId);
  return _jsx(TableCell, __assign({
    color: color !== null && color !== void 0 ? color : PLAN_COLORS[headerColor][0],
    sx: {
      minWidth: '17rem'
    },
    colSpan: 2,
    highlightBorder: highlightBorder
  }, {
    children: scenarioNameHeader ? _jsxs(Stack, __assign({
      direction: "row",
      alignItems: "center",
      justifyContent: "center"
    }, {
      children: [_jsxs(Stack, __assign({
        flex: 1,
        paddingLeft: isMultiskill ? 0 : '1.75rem'
      }, {
        children: [_jsxs(Stack, __assign({
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
          spacing: 0.5
        }, {
          children: [_jsx(Box, {
            children: title
          }), seatsExceeded && _jsx(SeatExceededIcon, {
            onClick: openAlertDetails
          })]
        })), isMultiskill && _jsx(Box, {
          children: t('report:multiskill')
        }), !!year && _jsxs(Box, {
          children: [t('translation:table.year'), " ", year]
        })]
      })), !isMultiskill && _jsx(EditIconButton, __assign({
        size: "small",
        color: "inherit",
        disabled: !permission,
        onClick: handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog.bind(null, DialogMode.RenameScenario, scenario)
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faPenToSquare
        })
      }))]
    })) : _jsx(_Fragment, {
      children: unit
    })
  }));
};
export var AnnualTableHead = function (_a) {
  var title = _a.title,
    handleOpenDialog = _a.handleOpenDialog,
    unit = _a.unit,
    expanded = _a.expanded,
    isExpandable = _a.isExpandable,
    onExpandToggle = _a.onExpandToggle,
    monoTone = _a.monoTone,
    scenarioNameHeader = _a.scenarioNameHeader,
    permission = _a.permission,
    sectionId = _a.sectionId;
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var theme = useTheme();
  if (!data) return null;
  return _jsx(TableHead, {
    children: _jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        stickySizes: STICKIES,
        id: sectionId
      }, {
        children: _jsxs(Stack, __assign({
          direction: "row",
          justifyContent: "space-between"
        }, {
          children: [title, isExpandable && _jsx(Tooltip, __assign({
            title: expanded ? t('collapse') : t('expand'),
            placement: "top"
          }, {
            children: _jsx(Link, __assign({
              component: "button",
              onClick: onExpandToggle,
              sx: {
                fontWeight: 'normal',
                color: 'white',
                ':hover': {
                  color: 'white'
                }
              }
            }, {
              children: _jsx(FontAwesomeIcon, {
                icon: expanded ? faMinimize : faMaximize
              })
            }))
          }))]
        }))
      })), data === null || data === void 0 ? void 0 : data.map(function (plan, indx, arr) {
        return _jsx(HeaderCell, {
          title: plan.name,
          headerColor: plan.colorIndex,
          color: monoTone ? theme.palette.text.primary : undefined,
          handleOpenDialog: handleOpenDialog,
          unit: unit,
          year: chunkSize === arr.length ? 0 : plan.year + 1,
          highlightBorder: indx + 1 !== arr.length && !((indx + 1) % chunkSize),
          isMultiskill: plan.dataType === QueueType.Multiskill,
          scenarioNameHeader: scenarioNameHeader,
          permission: permission,
          planId: plan.id
        }, "".concat(plan.name, "_").concat(plan.year, "_").concat(plan.dataType));
      })]
    })
  });
};
export var DefaultActionCell = function (props) {
  var _a, _b, _c, _d, _e, _f;
  var handleOpenDialog = props.handleOpenDialog,
    title = props.title,
    headerColor = props.headerColor,
    highlightBorder = props.highlightBorder,
    planId = props.planId,
    isMultiskill = props.isMultiskill,
    hasMultiskill = props.hasMultiskill,
    _g = props.showMultiskillAction,
    showMultiskillAction = _g === void 0 ? true : _g,
    _h = props.showSwitchAction,
    showSwitchAction = _h === void 0 ? true : _h,
    _j = props.showDeleteAction,
    showDeleteAction = _j === void 0 ? true : _j,
    _k = props.showHeadcountAction,
    showHeadcountAction = _k === void 0 ? true : _k;
  var t = useTranslation('report').t;
  var navigate = useNavigate();
  var scenarioByName = useSelectScenario(title);
  var reportData = useReportAPI()[0];
  var scenario = (_c = (_b = (_a = reportData.data) === null || _a === void 0 ? void 0 : _a.scenarios) === null || _b === void 0 ? void 0 : _b.find(function (item) {
    return item.id === planId;
  })) !== null && _c !== void 0 ? _c : scenarioByName;
  var baselinePlan = (_e = (_d = reportData.data) === null || _d === void 0 ? void 0 : _d.scenarios) === null || _e === void 0 ? void 0 : _e.find(function (item) {
    return item.isBaseline;
  });
  var isBaselineLocked = !!(baselinePlan === null || baselinePlan === void 0 ? void 0 : baselinePlan.isLocked);
  var isBaselineMultiskill = !!(isMultiskill && (scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline));
  var lockMessage = (scenario === null || scenario === void 0 ? void 0 : scenario.isLocked) && (scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) ? t('annual.lockedBy', {
    name: (_f = scenario === null || scenario === void 0 ? void 0 : scenario.lockedByUser) === null || _f === void 0 ? void 0 : _f.name
  }) : t('annual.lock');
  var multiSkillScenarios = useAllMultiskillScenariosAPI();
  var hasMultiskillForScenario = hasMultiskill || multiSkillScenarios.some(function (ms) {
    return ms.id === (scenario === null || scenario === void 0 ? void 0 : scenario.id);
  });
  var capacityPermission = useReportEffectivePermission();
  var isCapacityEdit = capacityPermission === StatusPermission.Edit;
  var canEdit = isCapacityEdit && (!(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) || !(scenario === null || scenario === void 0 ? void 0 : scenario.isLocked));
  var actions = [{
    key: 'multiskill',
    shouldRender: !isMultiskill && showMultiskillAction,
    shouldReserve: !isMultiskill && showMultiskillAction,
    render: _jsx(Tooltip, __assign({
      title: t('multiskill'),
      placement: "top"
    }, {
      children: _jsx(Link, __assign({
        disabled: hasMultiskillForScenario || !isCapacityEdit || !!((scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && (scenario === null || scenario === void 0 ? void 0 : scenario.isLocked)),
        component: "button",
        onClick: handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog.bind(null, DialogMode.Multiskill, scenario)
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faStarHalfStroke
        })
      }))
    })),
    placeholder: _jsx(Typography, __assign({
      visibility: "hidden"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: faStarHalfStroke
      })
    }))
  }, {
    key: 'lock',
    shouldRender: !!(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && !isMultiskill,
    shouldReserve: !!(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && !isMultiskill,
    render: _jsx(Tooltip, __assign({
      title: lockMessage,
      placement: "top"
    }, {
      children: _jsx(Link, __assign({
        component: "button",
        onClick: function () {
          handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog(DialogMode.LockPlan, scenario);
        },
        disabled: !isCapacityEdit
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: (scenario === null || scenario === void 0 ? void 0 : scenario.isLocked) && (scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) ? faLockKeyhole : faLockKeyholeOpen
        })
      }))
    })),
    placeholder: _jsx(Typography, __assign({
      visibility: "hidden"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: faLockKeyhole
      })
    }))
  }, {
    key: 'headcount-change',
    shouldRender: !isMultiskill && !(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && showHeadcountAction,
    shouldReserve: !isMultiskill && !(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline) && showHeadcountAction,
    render: _jsx(Tooltip, __assign({
      title: t('annual.headcountChange.title'),
      placement: "top"
    }, {
      children: _jsx(Link, __assign({
        component: "button",
        disabled: !isCapacityEdit || !!(scenario === null || scenario === void 0 ? void 0 : scenario.isLocked),
        onClick: handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog.bind(null, DialogMode.HeadcountChange, scenario)
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faUser
        })
      }))
    })),
    placeholder: _jsx(Typography, __assign({
      visibility: "hidden"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: faUser
      })
    }))
  }, {
    key: 'switch',
    shouldRender: !isMultiskill && showSwitchAction && !(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline),
    shouldReserve: !isMultiskill && showSwitchAction,
    render: _jsx(Tooltip, __assign({
      title: t('annual.switch'),
      placement: "top"
    }, {
      children: _jsx(Link, __assign({
        component: "button",
        disabled: !isCapacityEdit || isBaselineLocked,
        onClick: handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog.bind(null, DialogMode.SwitchScenario, scenario)
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faRightLeft
        })
      }))
    })),
    placeholder: _jsx(Typography, __assign({
      visibility: "hidden"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: faRightLeft
      })
    }))
  }, {
    key: 'delete',
    shouldRender: showDeleteAction && (isMultiskill || !(scenario === null || scenario === void 0 ? void 0 : scenario.isBaseline)),
    shouldReserve: showDeleteAction,
    render: _jsx(Tooltip, __assign({
      title: t('annual.delete'),
      placement: "top"
    }, {
      children: _jsx(Link, __assign({
        component: "button",
        disabled: !isCapacityEdit || !!(isBaselineMultiskill && (scenario === null || scenario === void 0 ? void 0 : scenario.isLocked)),
        onClick: handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog.bind(null, isMultiskill ? DialogMode.MultiskillDelete : DialogMode.DeleteScenario, scenario)
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faTrashCan
        })
      }))
    })),
    placeholder: _jsx(Typography, __assign({
      visibility: "hidden"
    }, {
      children: _jsx(FontAwesomeIcon, {
        icon: faTrashCan
      })
    }))
  }];
  var handleEdit = function () {
    if (isMultiskill) {
      navigate("/multiskill/".concat(scenario === null || scenario === void 0 ? void 0 : scenario.basePlanId, "/").concat(scenario === null || scenario === void 0 ? void 0 : scenario.id));
    } else if (isCapacityEdit && hasMultiskillForScenario) {
      handleOpenDialog === null || handleOpenDialog === void 0 ? void 0 : handleOpenDialog(DialogMode.DeleteAllMultiskill, scenario);
    } else {
      navigate("/wizard/".concat(scenario === null || scenario === void 0 ? void 0 : scenario.basePlanId, "/").concat(scenario === null || scenario === void 0 ? void 0 : scenario.id, "?step=").concat(WizardStep.Review));
    }
  };
  return _jsx(TableCell, __assign({
    color: isMultiskill ? MULTISKILL_COLOR : PLAN_COLORS[headerColor][1],
    colSpan: 2,
    highlightBorder: highlightBorder
  }, {
    children: _jsxs(Stack, __assign({
      direction: "row",
      alignItems: "center",
      spacing: 2,
      justifyContent: "space-between",
      sx: {
        px: 5
      }
    }, {
      children: [_jsx(Tooltip, __assign({
        title: canEdit ? t('annual.edit') : t('annual.view'),
        placement: "top"
      }, {
        children: _jsx(Link, __assign({
          component: "button",
          onClick: handleEdit
        }, {
          children: _jsx(FontAwesomeIcon, {
            icon: canEdit ? faSquarePen : faEye
          })
        }))
      })), actions.map(function (_a) {
        var key = _a.key,
          shouldRender = _a.shouldRender,
          shouldReserve = _a.shouldReserve,
          render = _a.render,
          placeholder = _a.placeholder;
        if (shouldRender) {
          return _jsx(Box, {
            children: render
          }, key);
        }
        if (shouldReserve) {
          return _jsx(Box, {
            children: placeholder
          }, "".concat(key, "-placeholder"));
        }
        return null;
      })]
    }))
  }));
};
var ResourcingActionCell = function (props) {
  return _jsx(DefaultActionCell, __assign({}, props));
};
var ActionRow = function (_a) {
  var handleOpenDialog = _a.handleOpenDialog,
    _b = _a.ActionCellComponent,
    ActionCellComponent = _b === void 0 ? DefaultActionCell : _b;
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  return _jsxs(TableRow, {
    children: [_jsx(FirstCell, {
      stickySizes: STICKIES
    }), data === null || data === void 0 ? void 0 : data.map(function (plan, indx, arr) {
      return _jsx(ActionCellComponent, {
        title: plan.name,
        planId: plan.id,
        headerColor: plan.colorIndex,
        handleOpenDialog: handleOpenDialog,
        highlightBorder: indx + 1 !== arr.length && !((indx + 1) % chunkSize),
        isMultiskill: plan.dataType === QueueType.Multiskill,
        hasMultiskill: plan.hasMultiskill
      }, "".concat(plan.name, "_").concat(plan.year, "_").concat(plan.dataType));
    })]
  });
};
var CostRow = function (_a) {
  var currency = _a.currency;
  var queueGroup = useSelectQueuesByCost();
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  return _jsx(_Fragment, {
    children: map(queueGroup, function (queues, name) {
      return _jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: name,
          stickySizes: STICKIES
        }), queues.map(function (queue, index, arr) {
          var _a;
          return _jsx(TableCell, {
            colSpan: 2,
            decimal: 2,
            color: (data === null || data === void 0 ? void 0 : data[index].dataType) === QueueType.Multiskill && queue.group ? MULTISKILL_COLOR : PLAN_COLORS[(_a = data === null || data === void 0 ? void 0 : data[index].colorIndex) !== null && _a !== void 0 ? _a : 0][1],
            value: queue.costPerWork,
            currency: currency,
            highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize)
          }, "".concat(name, "-").concat(queue.name || index));
        })]
      }, name);
    })
  });
};
var MainRow = function (_a) {
  var title = _a.title,
    _b = _a.color,
    color = _b === void 0 ? '#CCCCCC' : _b,
    keyItem = _a.keyItem,
    bold = _a.bold,
    monoTone = _a.monoTone;
  var data = useSummaryByDisplayMode();
  var currency = useSelectCurrency();
  var chunkSize = useSummaryChunkSize();
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var isCurrencyRow = !!keyItem && CURRENCY.includes(keyItem);
  var _c = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _c.prefix,
    currencySuffix = _c.suffix;
  var varianceOptions = {
    decimalPlaces: isCurrencyRow ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  if (!data) return null;
  return _jsx(_Fragment, {
    children: keyItem === 'costPerWork' ? _jsx(CostRow, {
      currency: currency
    }) : _jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        color: keyItem ? undefined : color,
        stickySizes: STICKIES,
        bold: bold
      }, {
        children: title
      })), times(data.length / chunkSize, function (year) {
        return times(chunkSize, function (n) {
          var _a;
          var index = n + year * chunkSize;
          var plan = data[index];
          var basePlan = data[year * chunkSize];
          // Determine cell color based on conditions
          var getCellColor = function () {
            var _a;
            if (monoTone) return color;
            if (data[index].dataType === QueueType.Multiskill && keyItem && plan[keyItem] !== null) {
              return MULTISKILL_COLOR;
            }
            return (_a = PLAN_COLORS[plan === null || plan === void 0 ? void 0 : plan.colorIndex]) === null || _a === void 0 ? void 0 : _a[1];
          };
          return _jsx(TableCell, {
            bold: bold,
            colSpan: 2,
            decimal: (_a = keyItem && DECIMAL_VALUE[keyItem]) !== null && _a !== void 0 ? _a : 0,
            color: getCellColor(),
            value: keyItem ? plan[keyItem] : undefined,
            percentage: keyItem && PERCENTAGES.includes(keyItem),
            currency: isCurrencyRow ? currency : undefined,
            highlightBorder: index !== data.length - 1 && n === chunkSize - 1,
            expandchildren: displayMode !== DisplayMode.All && keyItem && CALCULATE_DIFF.includes(keyItem) ? generateVariances(basePlan[keyItem], plan[keyItem], isPercentages, varianceOptions) : undefined,
            useColorValue: true
          }, "".concat(plan.name, "_").concat(plan.year, "_").concat(plan.dataType));
        });
      })]
    })
  });
};
var RoleTable = function (_a) {
  var keyItem = _a.keyItem;
  var roles = useSelectRoles(keyItem);
  var currency = useSelectCurrency();
  var isPercentages = useResourcingContext().isPercentages;
  var data = useSummaryByDisplayMode();
  var displayMode = useDisplayModeParams()[0];
  var isCurrencyRow = CURRENCY.includes(keyItem);
  var _b = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _b.prefix,
    currencySuffix = _b.suffix;
  var varianceOptions = {
    decimalPlaces: isCurrencyRow ? 0 : undefined,
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  if (!data) return null;
  return _jsx(_Fragment, {
    children: roles === null || roles === void 0 ? void 0 : roles.map(function (role) {
      return _jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: role.role,
          stickySizes: STICKIES
        }), role.value.map(function (values, year, arr) {
          return values.map(function (value, index) {
            var _a, _b;
            return _jsx(TableCell, {
              colSpan: 2,
              value: value,
              decimal: 1,
              color: data[index].dataType === QueueType.Multiskill ? MULTISKILL_COLOR : (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
              currency: isCurrencyRow ? currency : undefined,
              highlightBorder: year !== arr.length - 1 && index === values.length - 1,
              expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(keyItem) ? generateVariances(values[0], value, isPercentages, varianceOptions) : undefined
            }, "".concat(role.role, "-").concat(year, "-").concat(index));
          });
        })]
      }, role.role);
    })
  });
};
// Helper to check if occupancy value represents 100%
// Handles both decimal format (1 = 100%) and percentage format (100 = 100%)
export var isOccupancy100Percent = function (occupancy) {
  if (occupancy === undefined || occupancy === null) return false;
  // Check for decimal format (1) or percentage format (100)
  return occupancy === 1 || occupancy === 100;
};
var QueueRow = function (_a) {
  var queueName = _a.queueName,
    keys = _a.keys,
    _b = _a.hasSubItem,
    hasSubItem = _b === void 0 ? false : _b,
    constraintType = _a.constraintType;
  var queueData = useSelectQueuesByName(queueName, constraintType);
  var chunkSize = useSummaryChunkSize();
  var isPercentages = useResourcingContext().isPercentages;
  var data = useSummaryByDisplayMode();
  var displayMode = useDisplayModeParams()[0];
  var t = useTranslation('report', {
    keyPrefix: 'annual.reportRows'
  }).t;
  // Helper function to render empty cell for productive hours with 100% occupancy
  var renderEmptyProductiveHoursCell = function (key, queue, index, year, arr, queues) {
    var _a;
    if (!data || key !== 'productiveHours' || !('occupancy' in queue) || !isOccupancy100Percent(queue.occupancy)) {
      return null;
    }
    return _jsx(TableCell, {
      bold: !queue.metricTarget,
      color: queue.group && data[index].dataType === QueueType.Multiskill ? MULTISKILL_COLOR : (_a = PLAN_COLORS[data[index].colorIndex]) === null || _a === void 0 ? void 0 : _a[1],
      sx: {
        minWidth: hasSubItem ? '6.4rem' : undefined
      },
      colSpan: 2,
      highlightBorder: year !== arr.length - 1 && index === queues.length - 1
    }, "".concat(year, "_").concat(index));
  };
  if (!data || !queueData) return null;
  var shouldHideAbandonmentRateRow = keys.includes('abandonmentRate') && !queueData.some(function (queuesByYear) {
    return queuesByYear.some(function (q) {
      return q.isAbandonable;
    });
  });
  return _jsx(_Fragment, {
    children: hasSubItem ? _jsxs(_Fragment, {
      children: [_jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: queueName,
          bold: true,
          stickySizes: STICKIES
        }), queueData === null || queueData === void 0 ? void 0 : queueData.flat().map(function (q, index, arr) {
          var _a, _b;
          return _jsx(TableCell, {
            colSpan: 2,
            color: (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
            highlightBorder: index + 1 !== arr.length && !((index + 1) % chunkSize)
          }, "".concat(queueName, "-header-").concat(index));
        })]
      }), keys.map(function (key) {
        return key === 'abandonmentRate' && shouldHideAbandonmentRateRow ? null : _jsxs(TableRow, {
          children: [_jsx(FirstCell, {
            value: t(ANNUAL_REPORT_ROWS[key]),
            "aria-label": "2rem",
            stickySizes: STICKIES
          }), queueData === null || queueData === void 0 ? void 0 : queueData.map(function (queues, year, arr) {
            return queues.map(function (queue, index) {
              var _a, _b, _c, _d, _e;
              // Skip rendering productiveHours cell for queues with 100% occupancy
              var emptyCell = renderEmptyProductiveHoursCell(key, queue, index, year, arr, queues);
              if (emptyCell) {
                return _jsx(React.Fragment, {
                  children: emptyCell
                }, "".concat(year, "_").concat(index));
              }
              return _jsxs(React.Fragment, {
                children: [_jsx(TableCell, {
                  bold: !queue.metricTarget,
                  color: (_b = PLAN_COLORS[(_a = data[index]) === null || _a === void 0 ? void 0 : _a.colorIndex]) === null || _b === void 0 ? void 0 : _b[1],
                  sx: {
                    minWidth: '6.4rem'
                  },
                  value: queue[key],
                  percentage: PERCENTAGES.includes(key),
                  decimal: (_c = DECIMAL_VALUE[key]) !== null && _c !== void 0 ? _c : 0,
                  colSpan: key === 'serviceLevelForecast' ? 1 : 2,
                  highlight: queue.metricTarget && queue.metricTarget === metricTargetMap[key],
                  highlightBorder: key !== 'serviceLevelForecast' && year !== arr.length - 1 && index === queues.length - 1,
                  expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(key) ? generateVariances(queues[0][key], queue[key], isPercentages) : undefined,
                  useColorValue: true
                }), key === 'serviceLevelForecast' && _jsx(TableCell, {
                  sx: {
                    minWidth: '6.4rem'
                  },
                  value: queue.threshold,
                  suffix: ' sec',
                  color: (_e = PLAN_COLORS[(_d = data[index]) === null || _d === void 0 ? void 0 : _d.colorIndex]) === null || _e === void 0 ? void 0 : _e[1],
                  highlightBorder: year !== arr.length - 1 && index === queues.length - 1,
                  highlight: queue.metricTarget === metricTargetMap[key],
                  useColorValue: true
                })]
              }, "".concat(year, "_").concat(index));
            });
          })]
        }, key);
      })]
    }) : keys.map(function (key) {
      return key === 'abandonmentRate' && shouldHideAbandonmentRateRow ? null : _jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: queueName,
          stickySizes: STICKIES,
          bold: constraintType === ConstraintType.Multiskill,
          sx: {
            backgroundColor: constraintType === ConstraintType.Multiskill ? MULTISKILL_COLOR : ''
          }
        }), queueData === null || queueData === void 0 ? void 0 : queueData.map(function (queues, year, arr) {
          return queues.map(function (queue, index) {
            var _a;
            // Skip rendering productiveHours cell for queues with 100% occupancy
            var emptyCell = renderEmptyProductiveHoursCell(key, queue, index, year, arr, queues);
            if (emptyCell) {
              return emptyCell;
            }
            return _jsx(TableCell, {
              value: queue[key],
              color: queue.group && data[index].dataType === QueueType.Multiskill ? MULTISKILL_COLOR : PLAN_COLORS[data[index].colorIndex][1],
              colSpan: 2,
              percentage: PERCENTAGES.includes(key),
              decimal: (_a = DECIMAL_VALUE[key]) !== null && _a !== void 0 ? _a : 0,
              highlightBorder: year !== arr.length - 1 && index === queues.length - 1,
              expandchildren: displayMode !== DisplayMode.All && CALCULATE_DIFF.includes(key) ? generateVariances(queues[0][key], queue[key], isPercentages) : undefined,
              useColorValue: true
            }, "".concat(year, "_").concat(index));
          });
        })]
      }, key);
    })
  });
};
var ExpandRow = function (_a) {
  var data = _a.data,
    keyRow = _a.keyRow;
  var currency = useSelectCurrency();
  var chunkSize = useSummaryChunkSize();
  var columns = useMemo(function () {
    return data.reduce(function (acc, curr) {
      var agents = chain([curr === null || curr === void 0 ? void 0 : curr.defQueue, curr === null || curr === void 0 ? void 0 : curr.nonDefQueue, curr === null || curr === void 0 ? void 0 : curr.multiSkill]).flatten().orderBy(['queueType', 'order'], ['desc', 'asc']).map(function (_a) {
        var name = _a.name,
          value = _a.agentsSalaries,
          queueType = _a.queueType,
          group = _a.group,
          isMultiskill = _a.isMultiskill;
        return {
          name: name,
          value: value,
          queueType: queueType,
          group: group,
          isMultiskill: isMultiskill
        };
      }).value();
      var staff = curr.staffSalaries.filter(function (_a) {
        var role = _a.role;
        return role !== 'Agent';
      }).map(function (_a) {
        var name = _a.role,
          value = _a.value;
        return {
          name: name,
          value: value,
          group: 1
        };
      });
      acc.push(keyRow === 'agentsSalaries' ? agents : staff);
      return acc;
    }, []);
  }, [data, keyRow]);
  return _jsx(_Fragment, {
    children: columns[0].map(function (_a, rowIdx) {
      var name = _a.name,
        queueType = _a.queueType;
      return _jsxs(TableRow, {
        children: [_jsx(FirstCell, {
          value: name,
          "aria-label": "2rem",
          stickySizes: STICKIES,
          bold: queueType === QueueType.Multiskill,
          sx: {
            backgroundColor: queueType === QueueType.Multiskill ? MULTISKILL_COLOR : ''
          }
        }), columns.map(function (col, colIdx, arr) {
          var row = col[rowIdx];
          var useGroupedMultiskillColor = isGroupedMultiskillScenario(data[colIdx], row.group, row.isMultiskill);
          return _jsx(TableCell, {
            value: row.value,
            colSpan: 2,
            bold: row.queueType === QueueType.Multiskill,
            color: getGroupedMultiskillScenarioCellColor(data[colIdx], row.group, row.isMultiskill),
            decimal: 0,
            currency: currency,
            useColorValue: useGroupedMultiskillColor,
            highlightBorder: colIdx + 1 !== arr.length && !((colIdx + 1) % chunkSize)
          }, "".concat(rowIdx, "-").concat(colIdx));
        })]
      }, name);
    })
  });
};
var DataTableGroup = function (_a) {
  var _b;
  var keys = _a.keys,
    constraintType = _a.constraintType,
    both = _a.both,
    hideConstraintHeader = _a.hideConstraintHeader;
  var data = useSummaryByDisplayMode();
  var queues = constraintType === ConstraintType.Both ? undefined : (_b = data === null || data === void 0 ? void 0 : data[0]) === null || _b === void 0 ? void 0 : _b[constraintType];
  var t = useTranslation('report', {
    keyPrefix: 'annual.reportRows'
  }).t;
  if (!keys || !data) return null;
  return _jsxs(_Fragment, {
    children: [!!(queues === null || queues === void 0 ? void 0 : queues.length) && !hideConstraintHeader && _jsx(MainRow, {
      bold: true,
      monoTone: true,
      title: t(ANNUAL_REPORT_ROWS[constraintType])
    }), queues && sortQueuesByName(queues).map(function (queue) {
      return _jsx(QueueRow, {
        queueName: queue.name,
        keys: keys,
        hasSubItem: !both,
        constraintType: constraintType
      }, queue.name);
    }), constraintType === ConstraintType.Both && keys.map(function (key) {
      return INCLUDE_ROLE.includes(key) ? _jsx(RoleTable, {
        keyItem: key
      }, key) : _jsxs(React.Fragment, {
        children: [_jsx(MainRow, {
          title: t(ANNUAL_REPORT_ROWS[key]),
          keyItem: key,
          bold: true
        }), HIDE_ROW.includes(key) && _jsx(ExpandRow, {
          data: data,
          keyRow: key
        })]
      }, key);
    })]
  });
};
export var AnnualTableBody = function (_a) {
  var _b, _c;
  var handleOpenDialog = _a.handleOpenDialog,
    headerRows = _a.headerRows,
    _d = _a.both,
    both = _d === void 0 ? true : _d,
    _e = _a.hideConstraintHeader,
    hideConstraintHeader = _e === void 0 ? false : _e,
    actionCellComponent = _a.actionCellComponent,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var isServiceStandard = ((_b = headerRows.defQueue) === null || _b === void 0 ? void 0 : _b.includes('productivity')) || ((_c = headerRows.nonDefQueue) === null || _c === void 0 ? void 0 : _c.includes('serviceLevelForecast'));
  var isTotalBudgets = BUDGET_ROWS === headerRows;
  var renderContent = function () {
    if (isServiceStandard) {
      return _jsx(ServiceStandardSection, {
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        headerRows: headerRows
      });
    }
    if (isTotalBudgets) {
      return _jsx(StaffBudgetSection, {
        expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems['totalBudgets'],
        onToggle: onToggleItem ? function () {
          return onToggleItem('totalBudgets');
        } : undefined,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem
      });
    }
    return Object.keys(headerRows).map(function (key) {
      return _jsx(React.Fragment, {
        children: _jsx(DataTableGroup, {
          keys: headerRows[key],
          constraintType: key,
          both: both,
          hideConstraintHeader: hideConstraintHeader
        })
      }, key);
    });
  };
  return _jsxs(TableBody, {
    children: [handleOpenDialog && _jsx(ActionRow, {
      handleOpenDialog: handleOpenDialog,
      ActionCellComponent: actionCellComponent
    }), renderContent()]
  });
};
export var AnnualTable = function (props) {
  var summaryData = useSummaryReportAPI()[0];
  if (!summaryData.data) return null;
  return _jsx(TableContainer, __assign({
    topScroll: true
  }, {
    children: _jsx(Table, {
      children: props.children
    })
  }));
};
export var ResourcingTable = function (_a) {
  var handleOpenDialog = _a.handleOpenDialog;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var summaryData = useSummaryReportAPI()[0];
  var resourcingData = useSummaryByDisplayMode();
  var printData = usePrintTableAPI()[0];
  var exportData = useExportAPI()[0];
  var capacityPermission = useReportEffectivePermission();
  var lobsPerScenario = useSelectQueuesByLobPerScenario();
  var serviceExpansionItems = useMemo(function () {
    return lobsPerScenario.flatMap(function (lobEntry) {
      return __spreadArray([getLobExpansionKey(lobEntry.lobName), getConstraintExpansionKey(lobEntry.lobName, 'deferrable'), getConstraintExpansionKey(lobEntry.lobName, 'nonDeferrable')], getQueueExpansionKeysForLob(lobEntry), true);
    });
  }, [lobsPerScenario]);
  var queuesExpansion = useTableRowExpansion(serviceExpansionItems, true);
  var getNestedServiceExpansionItems = function (itemName) {
    var lobEntry = lobsPerScenario.find(function (entry) {
      return getLobExpansionKey(entry.lobName) === itemName;
    });
    if (lobEntry) {
      return __spreadArray([getConstraintExpansionKey(lobEntry.lobName, 'deferrable'), getConstraintExpansionKey(lobEntry.lobName, 'nonDeferrable')], getQueueExpansionKeysForLob(lobEntry), true);
    }
    var constraintEntry = lobsPerScenario.find(function (entry) {
      return [getConstraintExpansionKey(entry.lobName, 'deferrable'), getConstraintExpansionKey(entry.lobName, 'nonDeferrable')].includes(itemName);
    });
    if (!constraintEntry) return serviceExpansionItems.filter(function (item) {
      return item.startsWith("".concat(itemName, ":"));
    });
    var isDeferrable = itemName === getConstraintExpansionKey(constraintEntry.lobName, 'deferrable');
    return getQueueExpansionKeysForLob(__assign(__assign({}, constraintEntry), {
      template: __assign(__assign({}, constraintEntry.template), {
        defQueues: isDeferrable ? constraintEntry.template.defQueues : [],
        nonDefQueues: isDeferrable ? [] : constraintEntry.template.nonDefQueues
      })
    }));
  };
  var handleServiceToggleItem = function (itemName) {
    var expanded = !queuesExpansion.expandedItems[itemName];
    queuesExpansion.setItemsExpanded([itemName], expanded);
    queuesExpansion.setItemsExpanded(getNestedServiceExpansionItems(itemName), false);
  };
  var serviceRows = useMemo(function () {
    var _a;
    var hasAnyAbandonable = !!(resourcingData === null || resourcingData === void 0 ? void 0 : resourcingData.some(function (scenario) {
      var _a;
      return (_a = scenario.nonDefQueue) === null || _a === void 0 ? void 0 : _a.some(function (q) {
        return q.isAbandonable;
      });
    }));
    if (hasAnyAbandonable) return SERVICE_ROWS;
    return __assign(__assign({}, SERVICE_ROWS), {
      nonDefQueue: (_a = SERVICE_ROWS.nonDefQueue) === null || _a === void 0 ? void 0 : _a.filter(function (k) {
        return k !== 'abandonmentRate';
      })
    });
  }, [resourcingData]);
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: [summaryData.error, printData.error, exportData.error]
    }), _jsxs(AnnualTable, {
      children: [_jsx(AnnualTableHead, {
        handleOpenDialog: handleOpenDialog,
        title: t('serviceOrProductivity'),
        scenarioNameHeader: true,
        permission: capacityPermission === StatusPermission.Edit,
        isExpandable: true,
        expanded: queuesExpansion.someExpanded,
        onExpandToggle: queuesExpansion.toggleAll
      }), _jsx(AnnualTableBody, {
        handleOpenDialog: handleOpenDialog,
        headerRows: serviceRows,
        both: false,
        actionCellComponent: ResourcingActionCell,
        expandedItems: queuesExpansion.expandedItems,
        onToggleItem: handleServiceToggleItem
      }), _jsx(ProductionHoursTable, {}), _jsx(AgentRequirementTable, {}), _jsx(LearningCurveImpactTable, {}), _jsx(SeatsRequiredTable, {}), _jsx(SupportStaffTable, {})]
    })]
  });
};
export var FinancialTable = function (_a) {
  var handleOpenDialog = _a.handleOpenDialog;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var summaryData = useSummaryReportAPI()[0];
  var printData = usePrintTableAPI()[0];
  var exportData = useExportAPI()[0];
  var data = useSummaryByDisplayMode();
  var financialPermission = useUserPermissionsAPI().financialPermission;
  var effectiveFinancialPermission = useReportEffectivePlanPermission(financialPermission);
  useForbiddenRedirect([printData.statusCode, exportData.statusCode]);
  var lobsPerScenario = useSelectQueuesByLobPerScenario();
  var staffBudgetHierarchyItems = useMemo(function () {
    return lobsPerScenario.flatMap(function (lobEntry) {
      return __spreadArray([getLobExpansionKey(lobEntry.lobName)], getQueueExpansionKeysForLob(lobEntry), true);
    });
  }, [lobsPerScenario]);
  var multiskillExpansionItems = useMultiskillGroupExpansionKeys(data !== null && data !== void 0 ? data : []);
  var staffBudgetDefaultExpandedItems = useMemo(function () {
    return __spreadArray(__spreadArray(['totalBudgets'], lobsPerScenario.map(function (lobEntry) {
      return getLobExpansionKey(lobEntry.lobName);
    }), true), multiskillExpansionItems.length ? [getMultiskillCategoryExpansionKey()] : [], true);
  }, [lobsPerScenario, multiskillExpansionItems]);
  var staffBudgetExpansionItems = useMemo(function () {
    return __spreadArray(__spreadArray(['totalBudgets'], staffBudgetHierarchyItems, true), multiskillExpansionItems, true);
  }, [staffBudgetHierarchyItems, multiskillExpansionItems]);
  var staffBudgetExpansion = useTableRowExpansion(staffBudgetExpansionItems, false, staffBudgetDefaultExpandedItems);
  var handleStaffBudgetToggleItem = function (itemName) {
    var expanded = !staffBudgetExpansion.expandedItems[itemName];
    var nestedItems = itemName === 'totalBudgets' ? __spreadArray(__spreadArray([], staffBudgetHierarchyItems, true), multiskillExpansionItems, true) : getImplicitNestedExpansionItems(itemName, staffBudgetExpansionItems);
    staffBudgetExpansion.setItemsExpanded([itemName], expanded);
    staffBudgetExpansion.setItemsExpanded(Array.from(new Set(nestedItems)), false);
  };
  var handleStaffBudgetExpandToggle = function () {
    var expanded = !staffBudgetExpansion.someExpanded;
    staffBudgetExpansion.setItemsExpanded(['totalBudgets'], expanded);
    staffBudgetExpansion.setItemsExpanded(staffBudgetHierarchyItems, expanded);
    staffBudgetExpansion.setItemsExpanded(multiskillExpansionItems, expanded);
  };
  var currency = useSelectCurrency();
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: [summaryData.error, printData.error, exportData.error]
    }), _jsxs(AnnualTable, {
      children: [_jsx(AnnualTableHead, {
        handleOpenDialog: handleOpenDialog,
        title: t('staffBudget', {
          currency: currency
        }),
        scenarioNameHeader: true,
        permission: effectiveFinancialPermission === StatusPermission.Edit,
        isExpandable: true,
        expanded: staffBudgetExpansion.someExpanded,
        onExpandToggle: handleStaffBudgetExpandToggle
      }), _jsx(AnnualTableBody, {
        headerRows: BUDGET_ROWS,
        expandedItems: staffBudgetExpansion.expandedItems,
        onToggleItem: handleStaffBudgetToggleItem
      }), _jsx(AgentSalariesTable, {}), _jsx(SupportStaffSalariesTable, {}), _jsx(HiringAheadTable, {}), _jsx(OvertimeCostsTable, {}), _jsx(CostPerWorkTable, {})]
    })]
  });
};